import { sha256 } from "js-sha256"
import { Buffer } from "buffer"
import React, { useContext, useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { UserContext, VibratoClientContext } from "../contexts"
import { Phone } from "iconoir-react"

function LoadingStep({ text, subtext }: { text: string; subtext?: string }) {
  return (
    <div className="flex items-center gap-6 bg-white/50 backdrop-blur-sm rounded-xl p-6 w-full transition-all duration-300 hover:bg-white/60">
      <div className="relative">
        <div className="w-12 h-12 bg-linear-to-r from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
          <svg
            className="animate-spin h-6 w-6 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-purple-500 animate-pulse rounded-full" />
      </div>
      <div className="flex-1">
        <div className="text-2xl font-bold text-gray-900">{text}</div>
        {subtext && <div className="text-base text-gray-600 mt-2">{subtext}</div>}
      </div>
    </div>
  )
}

function CompletedStep({ text, subtext }: { text: string; subtext?: string }) {
  return (
    <div className="flex items-center gap-6 bg-white/50 backdrop-blur-sm rounded-xl p-6 w-full transition-all duration-300">
      <div className="relative">
        <div className="w-12 h-12 bg-linear-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
          <svg className="h-6 w-6 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full" />
      </div>
      <div className="flex-1">
        <div className="text-2xl font-bold text-gray-900">{text}</div>
        {subtext && <div className="text-base text-gray-600 mt-2">{subtext}</div>}
      </div>
    </div>
  )
}

function TaskDescriptionForm({ onSubmit }: { onSubmit: (description: string) => void }) {
  const [description, setDescription] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    onSubmit(description)
  }

  return (
    <div className="w-full p-8 bg-white rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Create a New Task</h2>
      <p className="text-gray-600 mb-6">
        Describe what you&apos;d like the AI to accomplish in this phone call. Be as specific as possible.
      </p>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Task Description
          </label>
          <textarea
            id="description"
            name="description"
            rows={4}
            className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 sm:text-base"
            placeholder="Example: Call my dentist and schedule a cleaning appointment for next week. Make sure to mention I'm a returning patient and prefer morning appointments."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || !description.trim()}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-linear-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Creating Task...
              </>
            ) : (
              "Create Task"
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default function FromDynamicTask(): React.JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { uuid } = useParams()
  const vibratoClient = useContext(VibratoClientContext)
  const user = useContext(UserContext)
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Function to generate cache key
  const generateCacheKey = (userId: string, taskDescription: string) => {
    const hash = sha256(taskDescription)
    return `vibrato_task_${userId}_${hash}`
  }

  // Function to check cache for existing task
  const getTaskFromCache = (taskDescription: string) => {
    if (!user?.id) return null
    const cacheKey = generateCacheKey(user.id, taskDescription)
    const cachedTask = localStorage.getItem(cacheKey)
    return cachedTask
  }

  // Function to save task to cache
  const saveTaskToCache = (taskDescription: string, taskUuid: string) => {
    if (!user?.id) return
    const cacheKey = generateCacheKey(user.id, taskDescription)
    localStorage.setItem(cacheKey, taskUuid)
  }

  const createDynamicTaskFromDescription = async (taskDescription: string) => {
    setIsProcessing(true)
    setError(null)

    try {
      // Check cache first
      const cachedTaskUuid = getTaskFromCache(taskDescription)

      if (cachedTaskUuid) {
        console.log("Using cached dynamic task:", cachedTaskUuid)
        setCurrentStep(2) // Skip task creation step
        const call = await vibratoClient.createCallFromDynamicTask(cachedTaskUuid)
        navigate(`/calls/${call.id}/edit`)
        return
      }

      // Create dynamic task if not in cache
      const dynamicTask = await vibratoClient.createDynamicTask({
        task_description: taskDescription,
        // recipient_name: "", // Will be filled in call settings
        // phone_number: "", // Will be filled in call settings
        country_code: "1", // Default to US
        metadata: null
      })

      // Save to cache
      saveTaskToCache(taskDescription, dynamicTask.uuid)

      setCurrentStep(2)

      // Create call from dynamic task
      const call = await vibratoClient.createCallFromDynamicTask(dynamicTask.uuid)

      // Navigate to call settings
      navigate(`/calls/${call.id}/edit`)
    } catch (e: unknown) {
      const error = e as Error
      setError(error.message)
      setIsProcessing(false)
    }
  }

  const createCallFromExistingTask = async (taskUuid: string) => {
    setIsProcessing(true)
    setError(null)
    try {
      setCurrentStep(2) // Skip task creation step since we already have a task
      const call = await vibratoClient.createCallFromDynamicTask(taskUuid)
      setCurrentStep(3)
      navigate(`/calls/${call.id}/edit`)
    } catch (e: unknown) {
      const error = e as Error
      setError(error.message)
      setIsProcessing(false)
    }
  }

  useEffect(() => {
    // Handle URL parameter case
    if (uuid) {
      createCallFromExistingTask(uuid)
      return
    }

    // Handle uuid from search params (alternative way to pass uuid)
    const uuidParam = searchParams.get("uuid")
    if (uuidParam) {
      createCallFromExistingTask(uuidParam)
      return
    }

    // Handle base64 encoded task description case
    const taskDescription = searchParams.get("task")
    if (!taskDescription) {
      return
    }

    try {
      // Decode the base64 task description
      const decodedTaskDescription = Buffer.from(taskDescription, "base64").toString("utf-8")
      createDynamicTaskFromDescription(decodedTaskDescription)
    } catch (e: unknown) {
      const error = e as Error
      setError(error.message)
    }
  }, [searchParams, uuid])

  if (error) {
    return (
      <div className="w-full">
        <div className="bg-white/80 backdrop-blur-sm border-l-4 border-red-500 p-6 rounded-xl shadow-xl">
          <div className="flex gap-4">
            <div className="shrink-0">
              <svg className="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Unable to Create Call</h3>
              <div className="mt-2 text-gray-600">{error}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!searchParams.get("task") && !isProcessing) {
    return (
      <div className="w-full p-8">
        <TaskDescriptionForm onSubmit={createDynamicTaskFromDescription} />
      </div>
    )
  }

  return (
    <div className="w-full p-8">
      <div className="mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <Phone width="24" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Setting Up Your Call</h1>
            <p className="text-gray-600">
              We're preparing everything to make your AI-powered call successful. This will just take a moment.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {currentStep >= 1 &&
          (currentStep === 1 ? (
            <LoadingStep
              text="Creating your task"
              subtext="Analyzing your requirements and preparing the AI assistant..."
            />
          ) : (
            <CompletedStep text="Task created successfully" subtext="Your AI assistant is ready to help" />
          ))}

        {currentStep >= 2 &&
          (currentStep === 2 ? (
            <LoadingStep text="Preparing your call" subtext="Setting up the communication channels..." />
          ) : (
            <CompletedStep text="Call prepared successfully" subtext="Communication channels are ready" />
          ))}

        {currentStep >= 3 && (
          <LoadingStep
            text="Finalizing setup"
            subtext="Taking you to call settings where you can review and customize your preferences..."
          />
        )}
      </div>

      <div className="mt-8 text-base text-gray-600">
        This process typically takes less than 30 seconds. We&apos;re making sure everything is perfectly set up for
        your call.
      </div>
    </div>
  )
}
